<template>
  <card>
    <template slot="header">
      <h5 v-if="$slots.title || title" class="card-title">
        <slot name="title">
          {{title}}
        </slot>
      </h5>
    </template>
    <div>
      <div class="loading-spinner" v-if="isLoading">
        <vue-loaders-ball-spin-fade-loader color="white" scale="1.2"/>
      </div>
      <div class="ct-chart" v-else>
        <v-chart class="chart" :option="option"/>
        <div class="estimate-text d-flex justify-content-between">
          <div class="d-flex now">
            <span class="type">Till Now: </span>
            <span class="value">{{ tillNow }}kwh</span>
          </div>
          <div class="d-flex">
            <span class="type">Predicted: </span>
            <span class="value">{{ predicted }}kwh</span>
          </div>
        </div>
      </div>
    </div>
  </card>
</template>
<script>

import Card from "./Card.vue";
import { mapActions, mapGetters } from "vuex";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
} from "echarts/components";
import VChart  from "vue-echarts";
import QuerySetting from '@/mixins/querySetting';
import OctoberData from '@/mixins/octoberData';

use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
]);


export default {
  name: "stats-card",
  mixins: [QuerySetting, OctoberData],
  props: {
    title: {
      type: String,
      default: ""
    }
  },
  components: {
    Card,
    VChart
  },
  computed: {
    ...mapGetters({
      periodTime: 'settime/getPeriodTime',
			currentDayPower			: 'influx/getCurrentDayPower',
			currentNightPower		: 'influx/getCurrentNightPower',
			predictDayPower			: 'influx/getPredictDayPower',
			predictNightPower		: 'influx/getPredictNightPower',
      defaultSiteId: 'auth/getDefaultSiteId',
      user: "auth/getUser"
    })
  },
  data() {
    return {
      isLoading: false,
      tillNow: 0,
      predicted: 0,
      isGenSite: false,
      option: {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          backgroundColor: "#484E69",
          borderColor: "#484E69",
          textStyle: {
            color: 'white'
          },
        },
        grid: {
          width: '78%',
          height: '58%',
          left: 60,
        },
        xAxis: [
          {
            type: 'time',
            axisLabel: {
              show: true,
              rotate: 90,
              textStyle: {
                color: 'white',
                fontSize: '12px',
                fontWeight: 200,
                fontFamily: 'Nunito Sans Light',
              }
            },
            axisLine: {
              lineStyle: {
                type: 'dashed'
              }
            },
            axisTick: {
              show: false
            }
          }
        ],
        yAxis: [
          {
            type: 'value',
            show: true,
            nameRotate: 90,
            name: 'kWh',
            nameLocation: 'middle',
            nameGap: 40,
            axisLabel: {
              show: true,
              color: 'white'
            },
            splitLine: {
              lineStyle: {
                color: 'rgba(255, 255, 255, 0.3)',
                type: 'dashed',
                width: 1
              }
            },
            splitNumber: 4,
            nameTextStyle: {
              verticalAlign: 'middle',
              color: 'white',
              fontSize: '13px',
              fontFamily: 'Nunito Sans Regular',
              fontWeight: 600,
            }
          }
        ],
        series: [
          {
            type: 'line',
            areaStyle: {
              opacity: 1
            },
						itemStyle: {
							borderColor: 'white',
							borderWidth: 2,
							color: '#D73E71'
						},
						markLine: {
							data: [
									{ name: "Imp Day 01", xAxis: 120, lineStyle: {color: 'white'}},
							],
						},
            symbol: 'none',
            data: []
          },
					{
						type: 'line',
						areaStyle: {
							opacity: 0.7,
							color: '#273D7F',
						},
						itemStyle: {
							color: '#273D7F'
						},
						symbol: 'none',
						data: []
					},
          {
						type: 'line',
						itemStyle: {
							color: '#FFFF00'
						},
						symbol: 'none',
						data: []
					}
        ]
      }
    }
  },
  watch: {
    async periodTime(newValue) {
    	this.$nextTick(() => {
				this.option.series[0].data = [];
				this.option.series[1].data = [];
				this.getCostData();
			});
    },
    async defaultSiteId(newValue) {
      this.$nextTick(() => {
        this.option.series[0].data = [];
        this.option.series[1].data = [];
        this.getCostData();
      });
    }
  },
  async mounted() {
    this.user.devices.forEach(item => {
      if (item.device_type == 'Electricity Gen (Wh)') this.isGenSite = true;
    });
    this.option.series[0].data = [];
    this.option.series[1].data = [];
    this.getCostData();

		this.$nextTick(() => {
      window.setInterval(() => {
				this.option.series[0].data = [];
        this.option.series[1].data = [];
        this.getCostData();
      },120000);
		});
  },
  methods: {
    ...mapActions({
			getDisplayData: 'influx/getDisplayData',
      getDisplayGenCircuitData: 'influx/getDisplayGenCircuitData'
    }),
    async getCostData(value) {
      this.isLoading = true;
			await this.getDisplayData('overall');
      let genPower = [];

      if (this.isGenSite) {
        genPower = await this.getDisplayGenCircuitData();
      }

      let currentDayPower = this.currentDayPower;
      let currentNightPower = this.currentNightPower;
      let predictDayPower = this.predictDayPower;
      let predictNightPower = this.predictNightPower;
			let currentPower = [];
			let predictPower = [];
     
			if (this.periodTime == 'today') {
				currentPower = currentNightPower.concat(currentDayPower);
				predictPower = predictNightPower.concat(predictDayPower);
			} else {
				currentDayPower.forEach((item, index) => {
					currentPower.push([
						item[0],
						!!currentNightPower[index]? item[1] + currentNightPower[index][1]:item[1]
					]);
				});
          if (predictDayPower.length >= predictNightPower.length) {
            predictDayPower.forEach((item, index) => {
              if (!!predictNightPower[index] && predictNightPower[index][0] === item[0]) {
                predictPower.push([
                  item[0],
                  predictNightPower[index][1] + item[1]
                ]);
              } else {
                predictPower.push([
                  item[0],
                  item[1]
                ]);
              }
            });
          }

          if (predictDayPower.length < predictNightPower.length) {
            predictNightPower.forEach((item, index) => {
              if (!!predictDayPower[index] && predictDayPower[index][0] === item[0]) {
                predictPower.push([
                  item[0],
                  predictDayPower[index][1] + item[1]
                ]);
              } else {
                predictPower.push([
                  item[0],
                  item[1]
                ]);
              }
            });
          }
			}

			let tillNow = 0, predict = 0;
			currentPower.forEach(item => tillNow += item[1]);
			predictPower.forEach(item => predict += item[1]);
     

			this.tillNow = tillNow.toFixed(2);
			this.predicted = (tillNow + predict).toFixed(2);

			currentPower.splice(-1);
			this.option.series[0].data = currentPower;
			let seriesData = [];
			seriesData.push(currentPower[currentPower.length - 1]);
			predictPower.forEach(item => {
				seriesData.push(item);
			});

			this.option.series[1].data = seriesData;
      this.option.series[2].data = genPower;
			this.isLoading = false;
    },
	}
};
</script>
<style lang="scss" scoped>
.estimate-text {
  position: absolute;
  top: 0px;
  left: 4%;
  text-align: center;

  .now {
    margin-right: 30px;
  }

  .type {
    font-size: 14px;
    font-family: "Nunito Sans Light";
  }

  .value {
    font-size: 14px;
    font-family: "Nunito Sans Regular";
    font-weight: bold !important;
  }

}
</style>
